import { useQuery } from '@tanstack/react-query'
import ms from 'ms'

import { QUERY_KEYS } from '@/lib/queryKeys'
import companyService from '@/lib/services/companyService'
import referralStatusService from '@/lib/services/referralStatusService'
import { ReferralStage, SelectOption } from '@/types/misc'
import { ReferralStatusCollectionItem } from '@/types/responses/referral-status'

export function useReferralStatusesOptions(
  companyId: number | null,
  importable: boolean = false,
) {
  const statusesQuery = useQuery({
    queryKey: QUERY_KEYS.referralStatuses(companyId),
    queryFn: () =>
      companyId
        ? companyService.getReferralStatuses(companyId)
        : referralStatusService.getDefaultStatuses(),
    refetchOnWindowFocus: false,
    staleTime: ms('2m'),
  })

  const statuses = statusesQuery.data?.['hydra:member'] || []

  const statusOptions = statuses
    .filter((status) =>
      importable ? status.importSelectable : status.selectable,
    )
    .map(
      (status): SelectOption => ({
        label: status.name,
        value: status['@id'],
      }),
    )

  return {
    options: statusOptions,
    isLoading: statusesQuery.isLoading,
    statuses,
    statusesMap: buildStatusesMap(statuses),
    defaultStageStatusMap: buildDefaultStageStatusMap(statuses),
  }
}

function buildDefaultStageStatusMap(
  statuses: ReferralStatusCollectionItem[],
): Map<ReferralStage, ReferralStatusCollectionItem> {
  const map = new Map<ReferralStage, ReferralStatusCollectionItem>()

  statuses.forEach((status) => {
    if (!map.has(status.stage)) {
      map.set(status.stage, status)
    }
  })

  return map
}

function buildStatusesMap(
  statuses: ReferralStatusCollectionItem[],
): Map<IRI, ReferralStatusCollectionItem> {
  const statusesMap = new Map<IRI, ReferralStatusCollectionItem>()
  const sortedStatuses = statuses.sort((a, b) => a.sortOrder - b.sortOrder)

  sortedStatuses.forEach((status) => {
    statusesMap.set(status['@id'], status)
  })

  return statusesMap
}
